"use client"

/**
 * Campus — the fifth home layout.
 *
 * Your App is only apps, and only their names. What distinguishes this variant
 * from the other four:
 *
 * 1. **School and program are workspace level.** One picker, under the
 *    greeting and under Administrator, stamps the pair onto every school-family
 *    product. It is the page's context line rather than a control on Your App.
 *    A school-family app that does not license the selected program moves to
 *    More from Exxat so Your App is only the campus that is open. Compliance
 *    and Curriculum Mapping stay in Your App.
 * 2. **Every app card is the same card.** Mark and name, one size, no cover
 *    art and no tagline.
 * 3. **Administrator sits in the greeting band.** It is a workspace console,
 *    not a program and not a campus app. People and Courses sit under their
 *    own **Workspace** section below Your App, on the same tile grid.
 *
 * One column, not a rail. Side by side, the catalogue sat at the same optical
 * weight as the apps the workspace owns, and capping both tracks to keep it
 * from outgrowing them spent the page width on margin. Stacked, reading order
 * carries the ranking instead: your apps, the workspace under them, then what
 * you do not have yet, then what changed.
 */

import * as React from "react"
import { Link } from "react-router"

import { useLoginSession } from "@/hooks/use-login-session"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import { selectProgramByChildId, useScopeSwitcher } from "@/components/scope-switcher-menu"
import { sessionMenuParent } from "@/lib/program-personas"
import { SCHOOL_ADMIN_COMPLIANCE_IFRAME_HREF } from "@/lib/school-admin-nav"
import {
  ensureSuperAdminAllProgramsScope,
  ensureTenantHomeAllProgramsScope,
  isAllProgramsScope,
  readSchoolFamilyScope,
  stampAllProgramsScope,
  stampSchoolFamilyScope,
  subscribeActiveScope,
} from "@/hooks/use-active-scope"
import type { Product } from "@/contexts/product-context"
import {
  getLoginSession,
  homeAllProgramsScopeOnly,
  openAs,
  openAsLabel,
} from "@/lib/login-session"
import { defaultRoleForProgram } from "@/lib/program-personas"
import { getProductBrand } from "@/lib/product-brand"
import { orderedDirectoryLaunchSummaries, type ProductHomeCard } from "@/lib/product-home"
import { productGlyph } from "@/lib/product-glyph"
import {
  isSchoolScopedProduct,
  productLicensesScope,
  type ScopeChild,
  type ScopeParent,
} from "@/lib/scope-switcher"
import { cn } from "@/lib/utils"
import { showsInstituteManagementOnHome } from "@/lib/institute-management-home"
import {
  canAccessAdminConsoleInProgram,
  isTenantAdmin,
} from "@/lib/workspace-role"
import { useAppStore } from "@/stores/app-store"

import { AdminAppCardDoor, useAdminAppScope } from "./admin-app-picker"
import { BetaAccessAction } from "./product-beta-access"
import { RequestAccessAction } from "./request-access-action"
import { ProductMarkTile } from "./product-mark-tile"
import { ROW_ARROW, STRETCHED } from "./home-mosaic"

import {
  openableDoors,
  openOwnedProduct,
  useOpenAsIdentity,
  usesIdentityDoors,
} from "./product-home-parts"
/** The surface both app cells share, so hero and supporting stay one family. */
export const CAMPUS_CELL =
  "group relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-md focus-within:shadow-md"

/**
 * How a card opens: straight through, forked into two identities, or via the
 * Administrator app picker.
 *
 * A hook rather than three copies of the same six lines, because the hero cell
 * and the supporting cell place the controls differently but must decide them
 * identically — a card that forks in the small cell and not in the big one
 * would be the same product with two different doors.
 */
export function useCampusDoor(card: ProductHomeCard, onOpen: (card: ProductHomeCard) => void) {
  const scope = useScopeSwitcher(card.product, card.customIndex)
  const programId = card.scoped ? (scope.child?.id ?? null) : null
  const doors = openableDoors(card, programId)
  const openAsIdentity = useOpenAsIdentity(onOpen)
  const appScope = useAdminAppScope(card)

  return {
    doors,
    forked: usesIdentityDoors(doors),
    appScope,
    open: () => openOwnedProduct(card, doors, openAsIdentity, onOpen, appScope),
    openAs: (role: (typeof doors)[number]) => openAsIdentity(card, role),
  }
}

type CampusDoor = ReturnType<typeof useCampusDoor>

/**
 * The product name, as a stretched link or as plain text.
 *
 * Forked cards name themselves and hand the opening to the identity buttons. A
 * stretched link over the whole cell plus two doors inside it would be three
 * targets to one product, and the stretched one wins every click that misses a
 * button.
 */
export function CampusDoorName({ card, door }: { card: ProductHomeCard; door: CampusDoor }) {
  if (door.forked) return <span className="min-w-0">{card.label}</span>
  return (
    <button type="button" aria-label={`Open ${card.label}`} className={STRETCHED} onClick={door.open}>
      {card.label}
    </button>
  )
}

/** Administrator's identity doors, when a card forks into two roles. */
export function CampusDoorActions({
  card,
  door,
  className,
}: {
  card: ProductHomeCard
  door: CampusDoor
  className?: string
}) {
  if (!door.forked) return null

  return (
    <div className={cn("flex shrink-0 flex-wrap justify-end gap-1.5", className)}>
      {door.doors.map(role => (
        <Button
          key={role}
          type="button"
          variant="outline"
          size="sm"
          aria-label={`Open as ${openAsLabel(role)} in ${card.label}`}
          onClick={() => door.openAs(role)}
        >
          As {openAsLabel(role)}
        </Button>
      ))}
    </div>
  )
}

/** Stacked identity doors for catalog shelf cards. */
export function CampusDoorFooter({ card, door }: { card: ProductHomeCard; door: CampusDoor }) {
  return <CampusDoorActions card={card} door={door} className="mt-3 min-w-0" />
}

/**
 * An owned app: mark and name, one shape for every card in the grid.
 *
 * No brand cover, and no tagline. An earlier pass gave every door a 64px band
 * of its own hue, which made the cards shout at one volume and left the reader
 * ranking them by colour preference; the mark carries the same identity in an
 * eighth of the area. The tagline went for a different reason — it is the
 * catalogue's sales line, and describing an app the workspace already owns is
 * noise. It stays in More from Exxat, where the reader has not bought yet.
 *
 * One size for all of them, deliberately. A pass that promoted the first app
 * to a 2×2 cell with its illustration ranked the grid, but the ranking was the
 * catalogue's order rather than anything the reader had asked for, and it made
 * the same product look like a different kind of thing to two workspaces.
 *
 * `glyphOnly` on the mark tile skips drawn SVG marks and renders solid Font
 * Awesome on the brand circle instead, the same language as the Workspace
 * section beside it. Switcher and other home variants keep the SVG set.
 */
export function CampusAppTile({
  card,
  onOpen,
  tag,
}: {
  card: ProductHomeCard
  onOpen: (card: ProductHomeCard) => void
  /** Entitlement or scope chip pinned to the tile corner (Catalog variant). */
  tag?: React.ReactNode
}) {
  const door = useCampusDoor(card, onOpen)
  const adminDoor = door.appScope && !door.forked

  const body = (
    <>
      {tag ? <div className="absolute end-3 top-3 z-10">{tag}</div> : null}
      <div className="flex min-w-0 items-center gap-3">
        <ProductMarkTile card={card} className="shrink-0" glyphOnly />
        <h3 className="min-w-0 flex-1 text-sm leading-tight font-semibold text-balance">
          {adminDoor || door.forked ? (
            <span className="min-w-0">{card.label}</span>
          ) : (
            <CampusDoorName card={card} door={door} />
          )}
        </h3>
        {door.forked ? (
          <CampusDoorActions card={card} door={door} />
        ) : (
          <i aria-hidden className={ROW_ARROW} />
        )}
      </div>
    </>
  )

  return (
    <li className="min-w-0 list-none">
      {adminDoor ? (
        <AdminAppCardDoor
          scope={door.appScope!}
          card={card}
          onOpen={onOpen}
          className={cn(CAMPUS_CELL, "relative block w-full p-4 text-start")}
        >
          {body}
        </AdminAppCardDoor>
      ) : (
        <div className={cn(CAMPUS_CELL, "relative p-4")}>{body}</div>
      )}
    </li>
  )
}

export type CampusCatalogTileReason = "upsell" | "off-campus" | "not-included"

/**
 * Catalog or inactive app in the Campus cell — same border tile as Your App,
 * with the catalogue tagline and Learn more (Campus v3 folds More from Exxat
 * into Your App without the illustrated shelf).
 */
export function CampusCatalogTile({
  card,
  reason,
}: {
  card: ProductHomeCard
  reason: CampusCatalogTileReason
}) {
  const learnMore = (
    <Button variant="outline" size="sm" asChild className="relative shrink-0">
      <Link to={`/home/${card.slug}`}>
        Learn more
        <span className="sr-only"> about {card.label}</span>
      </Link>
    </Button>
  )

  const trailingActions =
    card.stage === "beta" ? (
      <div className="flex flex-wrap justify-end gap-1.5">
        {learnMore}
        <BetaAccessAction label={card.label} variant="default" />
      </div>
    ) : (
      learnMore
    )

  return (
    <li className="min-w-0 list-none">
      <article className={cn(CAMPUS_CELL, "relative flex h-full flex-col gap-3 p-4")}>
        <div className="flex min-w-0 items-start gap-3">
          <ProductMarkTile card={card} className="shrink-0" glyphOnly />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <h3 className="min-w-0 text-sm font-semibold leading-tight text-balance">
              <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1">
                <span className="min-w-0">{card.label}</span>
                {reason === "off-campus" ? (
                  <StatusBadge
                    label="Other program"
                    tone="warning"
                    icon="fa-location-dot"
                    size="sm"
                    className="shrink-0"
                  />
                ) : null}
                {card.stage ? (
                  <StatusBadge status={card.stage} size="sm" className="shrink-0" />
                ) : null}
                {reason === "not-included" ? (
                  <StatusBadge
                    label="Not included"
                    tone="neutral"
                    size="sm"
                    className="shrink-0"
                  />
                ) : null}
              </span>
            </h3>
            {card.tagline ? (
              <p className="text-sm leading-relaxed text-muted-foreground text-pretty">
                {card.tagline}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-auto flex justify-end">{trailingActions}</div>
      </article>
    </li>
  )
}

/**
 * School-owned app without access for the active persona.
 */
export function CampusRequestAccessTile({ card }: { card: ProductHomeCard }) {
  const [requested, setRequested] = React.useState(false)

  return (
    <li className="min-w-0 list-none">
      <article className={cn(CAMPUS_CELL, "relative flex h-full flex-col gap-3 p-4")}>
        <div className="flex min-w-0 items-start gap-3">
          <ProductMarkTile card={card} className="shrink-0" glyphOnly />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <h3 className="min-w-0 text-sm font-semibold leading-tight text-balance">
              <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1">
                <span className="min-w-0">{card.label}</span>
                {card.stage ? (
                  <StatusBadge status={card.stage} size="sm" className="shrink-0" />
                ) : null}
                {!requested ? (
                  <StatusBadge label="Access required" tone="neutral" size="sm" className="shrink-0" />
                ) : null}
              </span>
            </h3>
            {card.tagline ? (
              <p className="text-sm leading-relaxed text-muted-foreground text-pretty">
                {card.tagline}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-auto flex justify-end">
          <RequestAccessAction
            productLabel={card.label}
            requested={requested}
            onRequested={() => setRequested(true)}
          />
        </div>
      </article>
    </li>
  )
}

/**
 * Student Exxat One row — product description and Try now, no Open door.
 */
export function CampusIncludedTile({
  card,
  onTry,
}: {
  card: ProductHomeCard
  onTry: (card: ProductHomeCard) => void
}) {
  return (
    <li className="min-w-0 list-none">
      <article className={cn(CAMPUS_CELL, "relative flex h-full flex-col gap-3 p-4")}>
        <div className="flex min-w-0 items-start gap-3">
          <ProductMarkTile card={card} className="shrink-0" glyphOnly />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <h3 className="min-w-0 text-sm font-semibold leading-tight text-balance">
              <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1">
                <span className="min-w-0">{card.label}</span>
                {card.stage ? (
                  <StatusBadge status={card.stage} size="sm" className="shrink-0" />
                ) : null}
              </span>
            </h3>
            {card.tagline ? (
              <p className="text-sm leading-relaxed text-muted-foreground text-pretty">
                {card.tagline}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-auto flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="relative shrink-0"
            aria-label={`Try now in ${card.label}`}
            onClick={() => onTry(card)}
          >
            Try now
            <i className="fa-light fa-arrow-right text-sm" aria-hidden="true" />
          </Button>
        </div>
      </article>
    </li>
  )
}

/** Shared four-column grid class for Campus door sections. */
export const CAMPUS_DOOR_GRID_CLASS =
  "grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 xl:grid-cols-4"

/**
 * The grid both door sections lay their cells on.
 *
 * Four columns, fixed. Not `ownedGridClassName`, which widens the track as
 * cards are added: every cell here is the same size, so a column count that
 * moved with the card count would make the same app a different width in two
 * workspaces — and would break Your App and Workspace onto different rhythms
 * whenever they held different numbers of cards.
 *
 * Shared rather than written twice because that is the whole claim the two
 * sections make: a door is a door, and the heading above it is what says
 * whether it opens an app or the records every app reads. An earlier pass drew
 * Workspace as a hairline list, which said the opposite — that its three items
 * were a different kind of object — and left the page with two unrelated
 * shapes stacked on each other.
 */
export function CampusDoorGrid({
  cards,
  labelledBy,
  onOpen,
}: {
  cards: ProductHomeCard[]
  labelledBy?: string
  onOpen: (card: ProductHomeCard) => void
}) {
  return (
    <ul aria-labelledby={labelledBy} className={CAMPUS_DOOR_GRID_CLASS}>
      {cards.map(card => (
        <CampusAppTile key={card.slug} card={card} onOpen={onOpen} />
      ))}
    </ul>
  )
}

/**
 * One school and program for the workspace, written onto every school-family
 * app so Open lands in the same campus. Reads Prism's unrestricted config so
 * the menu is the workspace's schools, not whichever product was last open.
 */
export function useWorkspaceSchoolSwitcher() {
  const bag = useScopeSwitcher("exxat-prism")
  const session = useLoginSession()
  const customProducts = useAppStore(s => s.customProducts)
  const canSelectAllPrograms = isTenantAdmin()
  const allProgramsScopeOnly = homeAllProgramsScopeOnly(session)

  const familyStored = React.useSyncExternalStore(
    subscribeActiveScope,
    readSchoolFamilyScope,
    () => null,
  )

  const defaultParent = bag.config.defaultParent

  React.useEffect(() => {
    if (!canSelectAllPrograms) return
    if (allProgramsScopeOnly) {
      ensureTenantHomeAllProgramsScope(bag.config)
      return
    }
    // A stored "All programs" scope is retired. The selector keeps the last program.
    if (familyStored && !isAllProgramsScope(familyStored)) return
    ensureSuperAdminAllProgramsScope(bag.config)
  }, [allProgramsScopeOnly, canSelectAllPrograms, familyStored, bag.config])

  // A role change can leave the picker on a school that role has no programs in.
  React.useEffect(() => {
    if (allProgramsScopeOnly) {
      ensureTenantHomeAllProgramsScope(bag.config)
      return
    }
    const school = sessionMenuParent(bag.config, familyStored?.parentId ?? bag.parent?.id)
    const programs = bag.config.childrenOf(school)
    if (programs.length === 0) return
    const currentFits =
      familyStored?.parentId === school.id &&
      programs.some(program => program.id === familyStored.childId)
    if (currentFits) return
    const next =
      programs.find(program => program.id === familyStored?.childId) ?? programs[0]
    if (!next) return
    stampSchoolFamilyScope(school, next, customProducts)
  }, [
    allProgramsScopeOnly,
    bag.config,
    bag.parent?.id,
    customProducts,
    familyStored,
  ])

  const allPrograms =
    allProgramsScopeOnly ||
    (canSelectAllPrograms && isAllProgramsScope(familyStored))

  const scopeSchool = React.useMemo(() => {
    if (!allPrograms) return bag.parent
    const parentId = familyStored?.parentId ?? bag.parent?.id
    return (
      bag.config.parents.find(entry => entry.id === parentId) ??
      bag.parent ??
      defaultParent
    )
  }, [allPrograms, bag.config.parents, bag.parent, defaultParent, familyStored?.parentId])

  const stamp = React.useCallback(
    (parent: ScopeParent, child: ScopeChild) => {
      stampSchoolFamilyScope(parent, child, customProducts)
      const role = defaultRoleForProgram(child.id)
      if (role) openAs(role)
    },
    [customProducts],
  )

  const selectAllPrograms = React.useCallback(() => {
    stampAllProgramsScope(scopeSchool ?? defaultParent)
  }, [defaultParent, scopeSchool])

  const effectiveParent = allPrograms ? scopeSchool : bag.parent
  const effectiveChild = allPrograms ? null : bag.child

  return {
    ...bag,
    allPrograms,
    allProgramsScopeOnly,
    canSelectAllPrograms,
    selectAllPrograms,
    selectScope: (parent: ScopeParent, child: ScopeChild) => {
      if (allProgramsScopeOnly) {
        stampAllProgramsScope(parent)
        return
      }
      stamp(parent, child)
    },
    parent: effectiveParent,
    child: effectiveChild,
    label: allPrograms ? "All programs" : bag.label,
    parentLabel: effectiveParent?.name ?? bag.parentLabel,
    status: allPrograms ? ("open" as const) : bag.status,
    ariaLabel: allPrograms
      ? `${(scopeSchool ?? defaultParent).name} · All programs. ${bag.config.ariaSuffix}`
      : bag.ariaLabel,
    selectParent: (parent: ScopeParent) => {
      if (allProgramsScopeOnly) {
        stampAllProgramsScope(parent)
        bag.showMain()
        return
      }
      const first = bag.config.childrenOf(parent)[0]
      if (!first) return
      stamp(parent, first)
      bag.showMain()
    },
    selectChild: (child: ScopeChild) => {
      if (allProgramsScopeOnly) return
      const parent = allPrograms ? (scopeSchool ?? defaultParent) : bag.parent
      if (!parent) return
      stamp(parent, child)
      bag.showMain()
    },
  }
}

/**
 * Campus Your App is the apps that open in the picker’s school and program.
 *
 * A school-family product that does not license that pair still belongs to the
 * workspace, so it moves to More from Exxat rather than disappearing. There it
 * is a catalog row: the existing `/home/:slug` marketing page, not Open. Site-family
 * apps stay: they have no program to miss. Compliance and Curriculum Mapping
 * stay too: they are campus apps in this workspace, and Open still asks when
 * the picker is on a program they do not serve. When More from Exxat is hidden there
 * is nowhere to move them, so they stay in Your App. That is the Student with
 * two apps and Student with admin access sessions.
 */
const CAMPUS_STAYS_IN_YOUR_APP: ReadonlySet<Product> = new Set([
  "exxat-compliance",
  "exxat-curriculum-mapping",
])

export function splitCampusOwned(
  owned: ProductHomeCard[],
  parentId: string | null,
  childId: string | null,
  moveOffCampus: boolean,
): { onCampus: ProductHomeCard[]; offCampus: ProductHomeCard[] } {
  if (!moveOffCampus || !parentId || !childId) {
    return { onCampus: owned, offCampus: [] }
  }

  // Tenet Admin, all apps: every owned merchandised app stays in Apps with Open,
  // not in the inactive Other program row for narrow scope grants.
  if (getLoginSession().entitleAllHomeProducts) {
    return { onCampus: owned, offCampus: [] }
  }

  const onCampus: ProductHomeCard[] = []
  const offCampus: ProductHomeCard[] = []
  for (const card of owned) {
    if (
      isSchoolScopedProduct(card.product) &&
      !CAMPUS_STAYS_IN_YOUR_APP.has(card.product) &&
      !productLicensesScope(card.product, parentId, childId)
    ) {
      offCampus.push(card)
    } else {
      onCampus.push(card)
    }
  }
  return { onCampus, offCampus }
}

/** The console card. Homes keep it in the greeting band, not in Your App. */
export const ADMIN_PRODUCT: Product = "exxat-admin"

export function campusAdministratorCard(
  owned: ProductHomeCard[],
  programId: string | null = null,
): ProductHomeCard | null {
  if (!canAccessAdminConsoleInProgram(programId)) return null
  return owned.find(card => card.product === ADMIN_PRODUCT) ?? null
}

/** Spotlight v5 workspace list. Tenet admins see it on any selected program. */
export function campusInstituteManagementCard(
  owned: ProductHomeCard[],
  _programId: string | null = null,
): ProductHomeCard | null {
  if (!showsInstituteManagementOnHome()) return null
  const admin = owned.find(card => card.product === ADMIN_PRODUCT)
  if (!admin) return null
  const { href: _consoleHref, ...adminWithoutHref } = admin
  return {
    ...adminWithoutHref,
    label: "Institute management",
    tagline: "Institute-wide compliance, sites, and cross-program visibility",
    badge: "All programs",
    ...(homeAllProgramsScopeOnly()
      ? { href: SCHOOL_ADMIN_COMPLIANCE_IFRAME_HREF }
      : {}),
  }
}

/** @deprecated Use {@link campusInstituteManagementCard}. */
export function campusInstituteAdminCard(owned: ProductHomeCard[]): ProductHomeCard | null {
  return campusInstituteManagementCard(owned)
}

/** Campus apps only. People and Courses live under Workspace, not Your App. */
export function campusAppCards(
  onCampus: ProductHomeCard[],
  includeRecords: boolean,
): ProductHomeCard[] {
  const apps = onCampus.filter(card => card.product !== ADMIN_PRODUCT)
  if (!includeRecords) return apps
  return [...apps, ...campusRecordCards(apps.map(card => card.product))]
}

/**
 * The shared record hubs as owned-app cards.
 *
 * They are already products (`exxat-people` for Student and Faculty Management,
 * `exxat-courses`, `exxat-programs`) with marks and grey tiles like
 * Administrator. Campus keeps
 * them off the catalog inventory (`PRODUCTS_OFF_HOME`) so Storefront does not
 * sell them, then lists them under Workspace with the same Open row Your App
 * uses. `href` is what stops Open as: these hubs are records, not a
 * school-family app with two identities. Personnel Management is site-scoped
 * and stays off these rows.
 */
const CAMPUS_RECORD_PRODUCT: Record<string, Product> = {
  "people-students": "exxat-people",
  "people-faculty": "exxat-people",
  courses: "exxat-courses",
  programs: "exxat-programs",
}

/** Skip a workspace row when the same product already sits in Your App. */
const CAMPUS_RECORD_OCCUPIED_DEDUPE: Partial<Record<string, Product>> = {
  courses: "exxat-courses",
  programs: "exxat-programs",
}

export function campusRecordCards(occupied: Product[] = []): ProductHomeCard[] {
  const taken = new Set(occupied)
  return orderedDirectoryLaunchSummaries().flatMap(object => {
    const product = CAMPUS_RECORD_PRODUCT[object.id]
    if (!product) return []
    const dedupeProduct = CAMPUS_RECORD_OCCUPIED_DEDUPE[object.id]
    if (dedupeProduct && taken.has(dedupeProduct)) return []
    return [
      {
        product,
        label: object.label,
        brandColor: getProductBrand(product)?.brandColor ?? "var(--muted-foreground)",
        // No count. Spotlight prints one under these two, and Campus tried it,
        // but a queue length on the home page is a number nobody asked for and
        // cannot act on from here — the hub itself states it in context.
        tagline: "",
        scopeLabel: object.scope,
        icon: productGlyph(product),
        tileGlyph:
          object.id === "people-students" || object.id === "people-faculty"
            ? object.icon
            : undefined,
        scoped: false,
        entitled: true,
        slug: object.id,
        href: object.href,
      },
    ]
  })
}
